import React, { useState } from "react";
import {
  TableRow,
  TableCell,
  Button,
  TextField,
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import { Edit, Delete, Save, Cancel } from "@mui/icons-material";
import { hasPermission, Permission } from "core/utils/roles";
import { useSnackbar } from "notistack";
import { DocumentCategory } from "app/services/types.generated";

interface DocumentCategoryRowProps {
  row: DocumentCategory;
  attributes: any;
  onEdit: (docId: string, updatedData: Partial<DocumentCategory>) => Promise<void>;
  onDelete: (docId: string) => Promise<void>;
}

const DocumentCategoryRow: React.FC<DocumentCategoryRowProps> = ({
  row,
  attributes,
  onEdit,
  onDelete,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<DocumentCategory>(row);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleEdit = () => {
    setIsEditing(true);
    setEditData(row);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditData(row);
  };

  const handleSave = async () => {
    try {
      await onEdit(row.docId, {
        id: editData.id,
        externalCategory: editData.externalCategory,
        externalSubCategory: editData.externalSubCategory,
        internalCategory: editData.internalCategory,
        internalFileCategory: editData.internalFileCategory,
      });
      setIsEditing(false);
      enqueueSnackbar("Document category updated successfully", { variant: "success" });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to update document category";
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  const handleDelete = async () => {
    try {
      await onDelete(row.docId);
      setDeleteDialogOpen(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to delete document category";
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  const handleInputChange = (field: keyof DocumentCategory, value: string) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <>
      <TableRow>
        <TableCell>
          {row.id}
        </TableCell>
        <TableCell>
          {isEditing ? (
            <TextField
              value={editData.externalCategory}
              onChange={(e) => handleInputChange("externalCategory", e.target.value)}
              size="small"
              fullWidth
            />
          ) : (
            row.externalCategory
          )}
        </TableCell>
        <TableCell>
          {isEditing ? (
            <TextField
              value={editData.externalSubCategory}
              onChange={(e) => handleInputChange("externalSubCategory", e.target.value)}
              size="small"
              fullWidth
            />
          ) : (
            row.externalSubCategory
          )}
        </TableCell>
        <TableCell>
          {isEditing ? (
            <TextField
              value={editData.internalCategory}
              onChange={(e) => handleInputChange("internalCategory", e.target.value)}
              size="small"
              fullWidth
            />
          ) : (
            row.internalCategory
          )}
        </TableCell>
        <TableCell>
          {isEditing ? (
            <TextField
              value={editData.internalFileCategory}
              onChange={(e) => handleInputChange("internalFileCategory", e.target.value)}
              size="small"
              fullWidth
            />
          ) : (
            row.internalFileCategory
          )}
        </TableCell>
        <TableCell>
          <Box display="flex" gap={1}>
            {isEditing ? (
              <>
                <Tooltip title="Save">
                  <IconButton onClick={handleSave} color="primary" size="small">
                    <Save />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel">
                  <IconButton onClick={handleCancel} size="small">
                    <Cancel />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <>
                {hasPermission("update:document-category" as Permission) && (
                  <Tooltip title="Edit">
                    <IconButton onClick={handleEdit} color="primary" size="small">
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}
                {hasPermission("delete:document-category" as Permission) && (
                  <Tooltip title="Delete">
                    <IconButton onClick={() => setDeleteDialogOpen(true)} color="error" size="small">
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
          </Box>
        </TableCell>
      </TableRow>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>
          <Typography variant="h4">Confirm Delete</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this document category mapping?
          </Typography>
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary">
              <strong>Intelliflo Category:</strong> {row.externalCategory}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <strong>Intelliflo Sub-Category:</strong> {row.externalSubCategory}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <strong>Hubwise Category:</strong> {row.internalCategory}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <strong>Hubwise File Category:</strong> {row.internalFileCategory}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DocumentCategoryRow;
